import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PlusIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { WhatsAppConversation, WhatsAppThreadContact, WhatsAppThreadMessage } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppSendRecipient } from '../../models/MasterAdmin/whatsappHub';
import { WHATSAPP_SEND_PATH } from '../../models/MasterAdmin/whatsappHub';
import {
  fetchInboxConversations,
  fetchInboxThread,
} from '../../services/MasterAdmin/whatsappCampaignService';
import WhatsAppInboxContactSidebar from './WhatsAppInboxContactSidebar';
import WhatsAppInboxMessageBubble from './WhatsAppInboxMessageBubble';
import { recipientKey } from './whatsappPreview';
import { formatDisplayPhone, formatWaTime, initials, waChatListCol, waContactCol, waThreadCol, waWaHeader } from './whatsappUi';

type FilterKey = 'all' | 'unread' | 'outbound';

const PAGE_SIZE = 40;

function conversationKey(c: Pick<WhatsAppConversation, 'countryCode' | 'phoneNumber'>) {
  return `${c.countryCode}|${c.phoneNumber}`;
}

const WhatsAppInboxPanel: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selected, setSelected] = useState<WhatsAppConversation | null>(null);
  const [contact, setContact] = useState<WhatsAppThreadContact | null>(null);
  const [messages, setMessages] = useState<WhatsAppThreadMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [notes, setNotes] = useState('');
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const fetchingMoreRef = useRef(false);
  const conversationsRef = useRef<WhatsAppConversation[]>([]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
      const timer = window.setTimeout(scrollToBottom, 60);
      return () => window.clearTimeout(timer);
    }
  }, [messages, selected, scrollToBottom]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadConversations = useCallback(
    async (options?: { silent?: boolean; append?: boolean }) => {
      const append = Boolean(options?.append);
      const silent = Boolean(options?.silent);

      if (append) {
        if (fetchingMoreRef.current || !hasMore) return;
        fetchingMoreRef.current = true;
        setLoadingMore(true);
      } else if (!silent) {
        setLoadingList(true);
      }

      try {
        const offset = append ? conversationsRef.current.length : 0;
        const res = await fetchInboxConversations({
          search: debouncedSearch || undefined,
          filter,
          limit: PAGE_SIZE,
          offset,
        });
        const rows = res.data || [];
        const nextHasMore = Boolean(res.meta?.hasMore);

        setConversations((prev) => {
          if (!append) {
            if (silent && prev.length > rows.length) {
              const firstKeys = new Set(rows.map(conversationKey));
              const rest = prev.filter((c) => !firstKeys.has(conversationKey(c)));
              return [...rows, ...rest];
            }
            return rows;
          }
          const seen = new Set(prev.map(conversationKey));
          const merged = [...prev];
          for (const row of rows) {
            const key = conversationKey(row);
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(row);
          }
          return merged;
        });
        setHasMore(nextHasMore);
      } catch {
        if (!silent) toast.error('Failed to load conversations');
      } finally {
        if (append) {
          fetchingMoreRef.current = false;
          setLoadingMore(false);
        } else if (!silent) {
          setLoadingList(false);
        }
      }
    },
    [debouncedSearch, filter, hasMore]
  );

  const loadThread = useCallback(async (conv: WhatsAppConversation, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoadingThread(true);
    try {
      const res = await fetchInboxThread(conv.countryCode, conv.phoneNumber);
      setContact(res.data.contact);
      setMessages(res.data.messages || []);
      setNotes(res.data.contact.notes || '');
    } catch {
      if (!options?.silent) toast.error('Failed to load conversation');
    } finally {
      if (!options?.silent) setLoadingThread(false);
    }
  }, []);

  const [searchParams] = useSearchParams();
  const targetPhoneParam = searchParams.get('phone') || searchParams.get('phoneNumber');

  useEffect(() => {
    setHasMore(false);
    void loadConversations();
  }, [debouncedSearch, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasMore || loadingList || loadingMore) return;
    const node = loadMoreRef.current;
    const root = listScrollRef.current;
    if (!node || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingList && !loadingMore) {
          void loadConversations({ append: true, silent: true });
        }
      },
      { root, rootMargin: '120px', threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingList, loadingMore, loadConversations, conversations.length]);

  useEffect(() => {
    if (!targetPhoneParam) return;
    const digits = targetPhoneParam.replace(/\D/g, '').slice(-10);
    if (digits.length < 10) return;

    const found = conversations.find(
      (c) => c.phoneNumber?.endsWith(digits) || c.phone?.endsWith(digits)
    );
    if (found) {
      setSelected(found);
      return;
    }

    void (async () => {
      try {
        const res = await fetchInboxThread('+91', digits);
        const data = res.data;
        if (!data?.contact) return;
        const synthetic = {
          countryCode: data.contact.countryCode || '+91',
          phoneNumber: data.contact.phoneNumber || digits,
          phone: data.contact.phone || `+91${digits}`,
          maskedPhone: data.contact.maskedPhone || `+91******${digits.slice(-4)}`,
          contactName: data.contact.displayName || 'Unknown',
          lastMessagePreview: data.messages?.[data.messages.length - 1]?.renderedBody || '',
          lastMessageAt: data.messages?.[data.messages.length - 1]?.createdAt || new Date().toISOString(),
          unreadCount: 0,
          recipientType: data.contact.recipientType,
          linkedUserId: data.contact.linkedUserId,
          linkedVendorId: data.contact.linkedVendorId,
          tags: data.contact.tags || [],
        };
        setSelected(synthetic);
      } catch {
        /* thread may not exist yet */
      }
    })();
  }, [targetPhoneParam, conversations]);

  useEffect(() => {
    if (selected) loadThread(selected);
    else {
      setContact(null);
      setMessages([]);
    }
  }, [selected, loadThread]);

  useEffect(() => {
    if (!selected) return undefined;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      void loadThread(selected, { silent: true });
      void loadConversations({ silent: true });
    };
    const timer = window.setInterval(refresh, 20000);
    return () => window.clearInterval(timer);
  }, [selected, loadThread, loadConversations]);

  const displayPhone = (countryCode: string, phoneNumber: string, phone?: string) =>
    phone || formatDisplayPhone(countryCode, phoneNumber);

  const composeRecipients = useMemo((): WhatsAppSendRecipient[] => {
    if (!selected) return [];
    return [
      {
        key: recipientKey(selected.countryCode, selected.phoneNumber),
        countryCode: selected.countryCode,
        phoneNumber: selected.phoneNumber,
        displayName: selected.contactName,
        maskedPhone: selected.maskedPhone,
      },
    ];
  }, [selected]);

  const selectedPhone = selected
    ? displayPhone(selected.countryCode, selected.phoneNumber, selected.phone)
    : '';

  const openComposeNew = () => {
    navigate(WHATSAPP_SEND_PATH);
  };

  const openComposeForSelected = () => {
    navigate(WHATSAPP_SEND_PATH, { state: { recipients: composeRecipients } });
  };

  const conversationPreview = (preview: string) => {
    const text = preview.trim();
    if (text.startsWith('[Template:')) {
      const rest = text.replace(/^\[Template:[^\]]+\]\s*/, '').trim();
      return rest || text;
    }
    return text;
  };

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className={`${waChatListCol} ${selected ? 'hidden sm:flex' : 'flex'}`}>
        <div className={`${waWaHeader} space-y-3`}>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Inbox</h2>
            <button
              type="button"
              onClick={openComposeNew}
              className="rounded-full bg-[#25D366] p-2 text-white shadow hover:bg-[#1da851]"
              title="New message"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats, contacts, phone..."
              className="w-full rounded-lg border-0 bg-white py-2 pl-9 pr-3 text-sm shadow-sm dark:bg-[#2a3942] dark:text-white"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {(['all', 'unread', 'outbound'] as FilterKey[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${
                  filter === f
                    ? 'bg-[#25D366] text-white'
                    : 'bg-white text-gray-600 dark:bg-[#2a3942] dark:text-gray-300'
                }`}
              >
                {f === 'all' ? 'All chats' : f}
              </button>
            ))}
          </div>
        </div>

        <div ref={listScrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {loadingList ? (
            <p className="p-4 text-sm text-gray-500">Loading chats...</p>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">
              No conversations yet. Sent templates and campaign recipients will appear here.
            </p>
          ) : (
            <>
              {conversations.map((c) => {
                const active =
                  selected?.countryCode === c.countryCode && selected?.phoneNumber === c.phoneNumber;
                return (
                  <button
                    key={`${c.countryCode}${c.phoneNumber}`}
                    type="button"
                    onClick={() => setSelected(c)}
                    className={`flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60 ${
                      active ? 'bg-[#f0f2f5] dark:bg-[#2a3942]/50' : ''
                    }`}
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#dfe5e7] text-sm font-semibold text-gray-700 dark:bg-[#6b7b85] dark:text-white">
                      {initials(c.contactName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {c.contactName}
                        </span>
                        <span className="shrink-0 text-[10px] text-gray-500">
                          {formatWaTime(c.lastMessageAt)}
                        </span>
                      </div>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {conversationPreview(
                          c.lastMessagePreview || displayPhone(c.countryCode, c.phoneNumber, c.phone)
                        )}
                      </p>
                      {c.unreadCount > 0 && (
                        <span className="mt-1 inline-flex rounded-full bg-[#25D366] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          {c.unreadCount}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {(loadingMore || hasMore) && (
                <div
                  ref={loadMoreRef}
                  className="flex items-center justify-center gap-2 py-3 text-xs text-gray-500"
                >
                  {loadingMore ? (
                    <>
                      <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                      Loading more…
                    </>
                  ) : (
                    'Scroll for more'
                  )}
                </div>
              )}
              {!hasMore && conversations.length > 0 && (
                <p className="px-4 py-3 text-center text-[11px] text-gray-400">
                  Showing all {conversations.length} contacts
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className={`${waThreadCol} ${selected ? 'flex' : 'hidden sm:flex'}`}>
        {!selected ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-gray-500">
            <div className="rounded-full bg-[#25D366]/10 p-6 text-[#25D366]">
              <PaperAirplaneIcon className="h-10 w-10" />
            </div>
            <p className="text-sm">Select a chat or start a new conversation</p>
          </div>
        ) : (
          <>
            <div className={`${waWaHeader} flex items-center justify-between border-b border-gray-200 dark:border-gray-700`}>
              <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  className="rounded-lg p-1 text-gray-600 hover:bg-white/60 sm:hidden dark:text-gray-300"
                  onClick={() => setSelected(null)}
                  aria-label="Back to chats"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#dfe5e7] text-sm font-semibold dark:bg-[#6b7b85] dark:text-white">
                  {initials(selected.contactName)}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{selected.contactName}</p>
                  <p className="truncate text-xs text-gray-500 dark:text-[#8696a0]">{selectedPhone}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => selected && loadThread(selected)}
                  className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-white/60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                  title="Refresh messages"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loadingThread ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={openComposeForSelected}
                  className="rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1da851]"
                >
                  Send template
                </button>
              </div>
            </div>

            <div
              ref={chatContainerRef}
              className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
              style={{
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4cdc4\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              }}
            >
              {loadingThread ? (
                <p className="text-center text-sm text-gray-500">Loading messages...</p>
              ) : messages.length === 0 ? (
                <p className="text-center text-sm text-gray-500">No messages yet</p>
              ) : (
                <div className="space-y-2">
                  {messages.map((m) => (
                    <WhatsAppInboxMessageBubble key={m.id} message={m} />
                  ))}
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-gray-200 bg-[#f0f2f5] px-4 py-3 dark:border-gray-700 dark:bg-[#202c33]">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                WhatsApp 24-hour window: use approved templates to message customers outside an active chat.
              </p>
            </div>
          </>
        )}
      </div>

      <div
        className={`${waContactCol} ${
          selected
            ? 'fixed inset-x-0 bottom-0 z-40 flex max-h-[55vh] rounded-t-2xl border-t shadow-xl lg:static lg:max-h-none lg:rounded-none lg:shadow-none'
            : 'hidden lg:flex'
        }`}
      >
        {selected && contact ? (
          <WhatsAppInboxContactSidebar
            countryCode={selected.countryCode}
            phoneNumber={selected.phoneNumber}
            displayPhone={selectedPhone}
            contact={contact}
            notes={notes}
            onNotesChange={setNotes}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-4 text-center text-sm text-gray-400">
            Select a chat to view contact details
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppInboxPanel;
